import Honeybadger from "@honeybadger-io/js"

// Shared fetch helper for every capacity-planning board mutation (drag/drop create, bar move/resize,
// span edit, lane/chip remove, allocation move). Centralises the request + UI-feedback contract that
// the individual Stimulus controllers used to each duplicate:
//
//   - dispatches `planning:busy` before the request and `planning:idle` when it settles, so the
//     board's progress bar (planning-busy) can show/hide regardless of which action triggered it,
//   - renders a returned Turbo Stream (including the server's `planning_flash` error stream),
//   - on any non-Turbo / failed response, surfaces an error toast via `planning:flash` so a slow or
//     rejected mutation is never silent,
//   - always resolves to `{ ok }` so callers can keep a dialog open or revert an optimistic drag.
//
// The toolbar's undo/redo buttons need no announcement from here: every mutation's Turbo Stream
// response re-renders them from the server's action stack (BaseController#undo_controls_stream).
//
// It never throws — a network error resolves to `{ ok: false }` after flashing + notifying Honeybadger.
export async function planningMutation({ url, method = "POST", body, errorMessage, component }) {
  const csrfToken = document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

  const options = {
    method,
    credentials: "same-origin",
    headers: {
      "X-CSRF-Token": csrfToken,
      Accept: "text/vnd.turbo-stream.html",
    },
  }
  if (body !== undefined) {
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify(body)
  }

  document.dispatchEvent(new CustomEvent("planning:busy"))
  try {
    const response = await fetch(url, options)
    const html = await response.text()
    const contentType = response.headers.get("Content-Type") || ""

    const isStream = contentType.includes("text/vnd.turbo-stream.html")
    if (isStream) {
      // A Turbo Stream — the success refresh, or the server's 422 `planning_flash` error stream.
      Turbo.renderStreamMessage(html)
    }

    if (!isStream && !response.ok) {
      flashError(errorMessage)
      Honeybadger.notify(`${component || "planning-mutation"} failed: ${response.status}`, {
        context: { level: "error", component: component || "planning-mutation" },
      })
    }
    return { ok: response.ok }
  } catch (error) {
    flashError(errorMessage)
    Honeybadger.notify(error, { context: { component: component || "planning-mutation" } })
    return { ok: false }
  } finally {
    document.dispatchEvent(new CustomEvent("planning:idle"))
  }
}

function flashError(message) {
  document.dispatchEvent(new CustomEvent("planning:flash", { detail: { message } }))
}
